import { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * SAGE Theme System.
 *
 * Two themes — `dark` (default, SAGE's native obsidian/fog atmosphere)
 * and `light` (a warm linen palette, not pure white). The theme is
 * applied by setting a `data-theme` attribute on <html>, which flips
 * a sweeping set of class-overrides in index.css. This means no JSX
 * file needs individual color changes — the entire visual system
 * swaps atomically.
 *
 * Persistence is in localStorage under `sage_theme` so a user's
 * preference survives reload. The provider also respects the OS-level
 * `prefers-color-scheme` the first time a user visits.
 */

const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

const STORAGE_KEY = 'sage_theme';

const readInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (err) {
    // Storage unavailable (incognito, strict Safari, sandboxed iframe).
    if (process.env.NODE_ENV !== 'production') console.debug('theme read:', err);
  }
  // Honor OS preference on first visit; most users still prefer dark here
  // because that's SAGE's native identity, so we default to dark unless
  // the OS explicitly asks for light.
  try {
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('match-media:', err);
  }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitialTheme);

  // Apply the attribute pre-paint on every change so there's no flash.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    // Tailwind's class-based dark mode: keep `.dark` on <html> when
    // theme === 'dark' so shadcn + custom dark-only utility classes
    // still resolve correctly. In light mode we remove it, letting
    // base-mode class values apply.
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (err) {
      // Storage write may fail in incognito / over-quota — the theme
      // still works for this tab, it just won't survive reload.
      if (process.env.NODE_ENV !== 'production') console.debug('theme write:', err);
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => (cur === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
